import React, { FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { API, Designer, Endpoints, GroupAPI } from '@brg/api';
import { Avatar, useToasts } from '@brg/ui';
import MoreIcon from 'shared/icons/MoreIcon';
import SearchIcon16 from 'shared/icons/SearchIcon16';
import { ReactComponent as CheckIcon } from 'shared/assets/check.svg';
import { computeDesigner } from 'shared/helpers/computeData';
import { LayoutConst, useModal } from 'core';
import {
  ConsultationScheduleOperation,
  TIMESLOT_LOCKED,
  generateConsultationSlots,
  useConsultationScheduleStudent,
} from 'shared/hooks/api/LMS/useConsultationScheduleStudent';

interface ConsultationRecord {
  start: string;
  end?: string;
  studentId?: string;
  userName?: string;
  groupName?: string;
  isBlocked?: boolean;
  isVisited?: boolean;
}

interface ConsultationLesson {
  id?: string;
  title?: string | Array<{ lang: string; name: string }>;
  start?: string;
  end?: string;
  groupIds?: string[];
  studentsMaxLimit?: number | null;
  timeSlotPerRerson?: number | null;
  signUpSheetIds?: Record<string, string> | null;
  signUpSheetModels?: Record<string, Record<string, unknown>> | null;
  attendanceSheetIds?: Record<string, string> | null;
  attendanceSheetModels?: Record<string, Record<string, unknown>> | null;
  consultation?: {
    capacity?: number;
    timePerPerson?: number;
    records?: ConsultationRecord[];
    unavailableSlots?: string[];
  };
}

type StudentOption = {
  id: string;
  accountId?: string;
  designerId?: string;
  name: string;
  avatar?: string;
  groupName: string;
};

export interface ConsultationRecordsModalProps {
  lesson: ConsultationLesson;
  courseGroups?: Array<{ groupId: string; name?: Array<{ lang: string; name: string }> }>;
  teamId?: string;
  onSuccess?: () => void;
}

const ModalWrapper = styled.div`
  width: 704px;
  max-width: calc(100vw - 32px);
  padding: 24px 32px 32px;
  border-radius: 8px;
  background: #ffffff;

  @media (max-width: ${LayoutConst.screen.s}px) {
    max-width: calc(100vw - 16px);
    padding: 24px 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  margin: 0 0 8px;
  color: #151514;
  font-family: Inter;
  font-size: 34px;
  font-weight: 600;
  line-height: 41px;
  letter-spacing: -0.68px;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #151514;
  font-family: Inter;
  font-size: 17px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: -0.34px;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #909090;
  cursor: pointer;
  font-size: 36px;
  font-weight: 300;
  line-height: 24px;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

const RecordsList = styled.div`
  max-height: min(576px, calc(100vh - 190px));
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const RecordRow = styled.div<{ $menuOpen?: boolean }>`
  position: relative;
  z-index: ${({ $menuOpen }) => ($menuOpen ? 2 : 0)};
  display: grid;
  grid-template-columns: 100px 24px minmax(0, 1fr) 100px 40px;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  border-bottom: 1px solid #b6b6b5;
  padding: 4px 0;

  @media (max-width: ${LayoutConst.screen.s}px) {
    grid-template-columns: 84px 24px minmax(0, 1fr) 32px;

    & > :nth-child(4) {
      display: none;
    }
  }
`;

const Cell = styled.div<{ $muted?: boolean }>`
  overflow: hidden;
  color: ${({ $muted }) => ($muted ? '#b6b6b5' : '#151514')};
  font-family: Inter;
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.26px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SearchWrap = styled.div`
  position: relative;
  width: 100%;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 40px;
  padding: 0 16px;
  border-radius: 3px;
  background: #f2f2f2;
  color: #909090;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: #151514;
  font-family: Inter;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: -0.26px;

  &::placeholder {
    color: #909090;
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const Suggestions = styled.div<{
  $top?: number;
  $bottom?: number;
  $left: number;
  $width: number;
  $maxHeight: number;
}>`
  position: fixed;
  z-index: 200001;
  top: ${({ $top }) => ($top == null ? 'auto' : `${$top}px`)};
  bottom: ${({ $bottom }) => ($bottom == null ? 'auto' : `${$bottom}px`)};
  left: ${({ $left }) => `${$left}px`};
  width: ${({ $width }) => `${$width}px`};
  max-height: ${({ $maxHeight }) => `${$maxHeight}px`};
  overflow-y: auto;
  border-radius: 3px;
  background: #ffffff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  scrollbar-width: thin;
  scrollbar-color: #b6b6b5 #e7e7e7;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #e7e7e7;
  }

  &::-webkit-scrollbar-thumb {
    background: #b6b6b5;
    border-radius: 2px;
  }
`;

const SuggestionItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: 0;
  background: transparent;
  color: #151514;
  font-family: Inter;
  font-size: 13px;
  line-height: 16px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f2f2f2;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const SuggestionAvatar = styled.div`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
`;

const SuggestionText = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
`;

const SuggestionName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SuggestionGroup = styled.span`
  overflow: hidden;
  color: #909090;
  font-size: 11px;
  line-height: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AttendanceCheckbox = styled.button<{ $checked?: boolean }>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid ${({ $checked }) => ($checked ? '#909090' : '#b6b6b5')};
  border-radius: 3px;
  background: #fefefe;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

const MoreButton = styled.button`
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #909090;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  .moreIcon {
    width: 24px;
    height: 24px;
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border: 0;
    box-shadow: none;
  }
`;

const MenuButton = styled.button<{ $openUp?: boolean }>`
  position: absolute;
  z-index: 1;
  right: 0;
  ${({ $openUp }) => ($openUp
    ? 'bottom: calc(100% - 1px); top: auto;'
    : 'top: calc(100% - 1px); bottom: auto;')}
  width: 240px;
  padding: 12px 16px;
  border: 0;
  background: #f2f2f2;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.12);
  color: #70706f;
  font-family: Inter;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;

  &:focus,
  &:focus-visible {
    outline: none;
    border: 0;
    box-shadow: none;
  }
`;

const getTitle = (lesson: ConsultationLesson, language: string) => {
  if (typeof lesson.title === 'string') return lesson.title;

  return lesson.title?.find((item) => item.lang === language.toLowerCase())?.name
    || lesson.title?.[0]?.name
    || '';
};

const getStudentId = (student: Record<string, unknown>) =>
  String(student.accountId || student.designerId || student.id || '');

const getStudentName = (student: Record<string, unknown>, language: string) => {
  const localized = API.toLang(student, language) as Designer;
  const computed = computeDesigner(localized, { showFullName: true });
  const computedName = [computed.surName, computed.name, computed.middleName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  if (computedName) return computedName;

  if (typeof student.fullName === 'string' && student.fullName.trim()) return student.fullName.trim();
  if (typeof student.name === 'string' && student.name.trim()) return student.name.trim();
  if (Array.isArray(student.name)) {
    return String((student.name[0] as { name?: string } | undefined)?.name || '').trim();
  }

  return [student.lastName, student.firstName, student.middleName]
    .filter((value): value is string => typeof value === 'string' && Boolean(value.trim()))
    .join(' ');
};

const mapStudentOption = (student: Designer<'Global'> | Designer<'Local'>, groupName: string, language: string): StudentOption | null => {
  const localized = API.toLang(student, language) as Designer;
  const raw = localized as unknown as Record<string, unknown>;
  const computed = computeDesigner(localized, { showFullName: true });
  const id = getStudentId(raw);
  const name = getStudentName(raw, language);

  if (!id || !name) return null;

  return {
    id,
    accountId: raw.accountId ? String(raw.accountId) : undefined,
    designerId: raw.designerId ? String(raw.designerId) : undefined,
    name,
    avatar: computed.avatar,
    groupName,
  };
};

const isRecordVisited = (lesson: ConsultationLesson, timeSlot: string) => Boolean(
  lesson.attendanceSheetIds?.[timeSlot] || lesson.attendanceSheetModels?.[timeSlot],
);

const findStudentOption = (students: StudentOption[], studentId?: string) => {
  if (!studentId) return null;
  return students.find((item) => (
    item.id === studentId
    || item.accountId === studentId
    || item.designerId === studentId
  )) || null;
};

const buildRecordsFromLesson = (lesson: ConsultationLesson, language: string): ConsultationRecord[] => {
  const models = Object.entries(lesson.signUpSheetModels || {}).map(([timeSlot, student]) => ({
    start: timeSlot,
    studentId: getStudentId(student) || undefined,
    userName: getStudentName(student, language) || undefined,
    isVisited: isRecordVisited(lesson, timeSlot),
  }));
  const modelSlotTimes = new Set(
    models
      .map((record) => new Date(record.start).getTime())
      .filter((value) => !Number.isNaN(value)),
  );
  const ids = Object.entries(lesson.signUpSheetIds || {})
    .map(([timeSlot, studentId]) => ({
      start: timeSlot,
      studentId,
      isVisited: isRecordVisited(lesson, timeSlot),
    }))
    .filter((record) => {
      const time = new Date(record.start).getTime();
      return !Number.isNaN(time) && !modelSlotTimes.has(time);
    });

  return [...models, ...ids];
};

export const ConsultationRecordsModal: FC<ConsultationRecordsModalProps> = ({
  lesson,
  courseGroups = [],
  teamId,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation();
  const modal = useModal();
  const toasts = useToasts();
  const { scheduleStudent, isLoading } = useConsultationScheduleStudent();
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [activeSearchSlotId, setActiveSearchSlotId] = useState<string | null>(null);
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const [students, setStudents] = useState<StudentOption[]>([]);
  const [localRecords, setLocalRecords] = useState<ConsultationRecord[]>(() => (
    buildRecordsFromLesson(lesson, i18n.language)
  ));
  const [dropdownPosition, setDropdownPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const searchWrapRef = useRef<HTMLDivElement | null>(null);
  const suggestionsRef = useRef<HTMLDivElement | null>(null);
  const recordsListRef = useRef<HTMLDivElement | null>(null);
  const start = useMemo(() => (lesson.start ? new Date(lesson.start) : null), [lesson.start]);
  const duration = lesson.consultation?.timePerPerson || lesson.timeSlotPerRerson || 15;
  const maxSlots = lesson.consultation?.capacity ?? lesson.studentsMaxLimit ?? null;
  const specificGroupIds = useMemo(
    () => (lesson.groupIds || []).filter(Boolean),
    [lesson.groupIds],
  );

  const updateDropdownPosition = () => {
    const anchor = searchWrapRef.current;
    if (!anchor) {
      setDropdownPosition(null);
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const gap = 4;
    const preferredHeight = 220;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < preferredHeight && spaceAbove > spaceBelow;
    const maxHeight = Math.max(80, Math.min(preferredHeight, openUp ? spaceAbove : spaceBelow));

    if (openUp) {
      setDropdownPosition({
        bottom: window.innerHeight - rect.top + gap,
        left: rect.left,
        width: rect.width,
        maxHeight,
      });
      return;
    }

    setDropdownPosition({
      top: rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      maxHeight,
    });
  };

  useEffect(() => {
    setLocalRecords(buildRecordsFromLesson(lesson, i18n.language));
  }, [i18n.language, lesson.signUpSheetIds, lesson.signUpSheetModels, lesson.attendanceSheetIds, lesson.attendanceSheetModels]);

  useEffect(() => {
    let isCancelled = false;

    const loadStudents = async () => {
      try {
        if (specificGroupIds.length > 0) {
          const results = await Promise.all(specificGroupIds.map(async (groupId) => {
            const response = await GroupAPI.GetMembersPaging({ groupId, limit: 0, page: 0 });
            const data = await response.json();
            const group = courseGroups.find((item) => item.groupId === groupId);
            const groupName = group?.name?.find((item) => item.lang === i18n.language)?.name
              || group?.name?.[0]?.name
              || '';

            return (data?.data || [])
              .map((student: Designer<'Global'> | Designer<'Local'>) => mapStudentOption(student, groupName, i18n.language))
              .filter((student: StudentOption | null): student is StudentOption => Boolean(student));
          }));

          if (isCancelled) return;
          const uniqueStudents = new Map<string, StudentOption>();
          results.flat().forEach((student) => uniqueStudents.set(student.id, student));
          setStudents(Array.from(uniqueStudents.values()));
          return;
        }

        if (!teamId) {
          if (!isCancelled) setStudents([]);
          return;
        }

        // One request: course groups + members, so we can show group name for each student.
        const response = await API.request()
          .endpoint(Endpoints.geograffee)
          .url('/api/LMS/GetGroupsInfoByCourseId')
          .addQuery('teamId', teamId)
          .get()
          .send();
        const data = await response.json();

        if (isCancelled) return;

        const uniqueStudents = new Map<string, StudentOption>();
        const resolveGroupName = (group: Record<string, unknown>) => {
          const fromCourse = courseGroups.find((item) => item.groupId === group.id || item.groupId === group.groupId);
          if (fromCourse) {
            return fromCourse.name?.find((item) => item.lang === i18n.language)?.name
              || fromCourse.name?.[0]?.name
              || '';
          }

          if (typeof group.title === 'string') return group.title;
          if (Array.isArray(group.name)) {
            const names = group.name as Array<{ lang?: string; name?: string }>;
            return names.find((item) => item.lang === i18n.language)?.name
              || names[0]?.name
              || '';
          }
          if (typeof group.name === 'string') return group.name;
          return '';
        };

        (data?.groups || []).forEach((group: Record<string, unknown>) => {
          const groupName = resolveGroupName(group);
          const members = (group.members || []) as Array<Designer<'Global'> | Designer<'Local'>>;
          members.forEach((student) => {
            const option = mapStudentOption(student, groupName, i18n.language);
            if (option) uniqueStudents.set(option.id, option);
          });
        });

        ((data?.noneGroupMembers || []) as Array<Designer<'Global'> | Designer<'Local'>>).forEach((student) => {
          const option = mapStudentOption(student, '', i18n.language);
          if (option && !uniqueStudents.has(option.id)) {
            uniqueStudents.set(option.id, option);
          }
        });

        setStudents(Array.from(uniqueStudents.values()));
      } catch {
        if (!isCancelled) setStudents([]);
      }
    };

    loadStudents();

    return () => {
      isCancelled = true;
    };
  }, [courseGroups, i18n.language, specificGroupIds, teamId]);

  useEffect(() => {
    if (!students.length) return;

    setLocalRecords((prev) => prev.map((record) => {
      if (!record.studentId || record.studentId === TIMESLOT_LOCKED) return record;
      if (record.userName && record.groupName) return record;

      const student = findStudentOption(students, record.studentId);
      if (!student) return record;

      return {
        ...record,
        userName: record.userName || student.name,
        groupName: record.groupName || student.groupName,
      };
    }));
  }, [students]);

  useLayoutEffect(() => {
    if (!activeSearchSlotId) {
      setDropdownPosition(null);
      return undefined;
    }

    updateDropdownPosition();

    const handleReposition = () => updateDropdownPosition();
    const listNode = recordsListRef.current;

    window.addEventListener('resize', handleReposition);
    listNode?.addEventListener('scroll', handleReposition, true);
    document.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      listNode?.removeEventListener('scroll', handleReposition, true);
      document.removeEventListener('scroll', handleReposition, true);
    };
  }, [activeSearchSlotId, searchQueries, students]);

  useEffect(() => {
    if (!activeSearchSlotId) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (searchWrapRef.current?.contains(target) || suggestionsRef.current?.contains(target)) {
        return;
      }
      setActiveSearchSlotId(null);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [activeSearchSlotId]);

  const slots = useMemo(() => {
    return generateConsultationSlots({
      start: lesson.start,
      end: lesson.end,
      durationMinutes: duration,
      maxSlots,
      referenceStart: lesson.start,
    }).map((slot) => ({
      ...slot,
      record: localRecords.find((item) => {
        const recordStart = new Date(item.start);
        return !Number.isNaN(recordStart.getTime()) && recordStart.getTime() === slot.time;
      }),
    }));
  }, [duration, lesson.end, lesson.start, localRecords, maxSlots]);

  const assignedStudentIds = useMemo(
    () => new Set(
      localRecords
        .map((record) => record.studentId)
        .filter((studentId): studentId is string => Boolean(studentId) && studentId !== TIMESLOT_LOCKED),
    ),
    [localRecords],
  );

  const date = start?.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }) || '';

  const handleScheduleStudent = async (
    timeSlot: string,
    studentId: string | undefined,
    operation: ConsultationScheduleOperation,
    studentMeta?: StudentOption,
  ) => {
    if (!lesson.id || (operation === ConsultationScheduleOperation.Add && !studentId)) return;

    const result = await scheduleStudent({
      lessonId: lesson.id,
      timeSlot,
      studentId: studentId ?? '',
      operation,
    });

    if (!result.success) {
      toasts.error(t('lms.consultation.recordsModal.error'));
      return;
    }

    if (operation === ConsultationScheduleOperation.Remove) {
      setLocalRecords((prev) => prev.filter((record) => record.start !== timeSlot));
    } else if (studentId === TIMESLOT_LOCKED) {
      setLocalRecords((prev) => [
        ...prev.filter((record) => record.start !== timeSlot),
        { start: timeSlot, studentId: TIMESLOT_LOCKED },
      ]);
    } else if (studentId) {
      setLocalRecords((prev) => [
        ...prev.filter((record) => record.start !== timeSlot),
        {
          start: timeSlot,
          studentId,
          userName: studentMeta?.name,
          groupName: studentMeta?.groupName,
        },
      ]);
    }

    setActiveMenu(null);
    setActiveSearchSlotId(null);
    setSearchQueries((prev) => {
      if (!(timeSlot in prev)) return prev;
      const next = { ...prev };
      delete next[timeSlot];
      return next;
    });
    toasts.info(t('lms.consultation.recordsModal.success'));
    onSuccess?.();
  };

  const handleToggleAttendance = async (timeSlot: string, studentId: string, nextVisited: boolean) => {
    if (!lesson.id || !studentId || studentId === TIMESLOT_LOCKED) return;

    setLocalRecords((prev) => prev.map((record) => (
      record.start === timeSlot ? { ...record, isVisited: nextVisited } : record
    )));

    const result = await scheduleStudent({
      lessonId: lesson.id,
      timeSlot,
      studentId,
      operation: nextVisited
        ? ConsultationScheduleOperation.Add
        : ConsultationScheduleOperation.Remove,
    }, '/api/LMS/ConsultationAttendanceStudent');

    if (!result.success) {
      setLocalRecords((prev) => prev.map((record) => (
        record.start === timeSlot ? { ...record, isVisited: !nextVisited } : record
      )));
      toasts.error(t('lms.consultation.recordsModal.error'));
    }
  };

  return (
    <ModalWrapper>
      <Header>
        <div>
          <Title>{t('lms.consultation.recordsModal.title')}</Title>
          <Subtitle>
            {[getTitle(lesson, i18n.language), date]
              .map((part) => part?.trim())
              .filter(Boolean)
              .join('. ')}
          </Subtitle>
        </div>
        <CloseButton type="button" onClick={() => modal.hide()} aria-label="close">×</CloseButton>
      </Header>

      <RecordsList ref={recordsListRef}>
        {slots.map((slot, index) => {
          const isBlocked = slot.record?.studentId === TIMESLOT_LOCKED;
          const student = findStudentOption(students, slot.record?.studentId);
          const userName = slot.record?.userName || student?.name;
          const groupName = slot.record?.groupName || student?.groupName || '';
          const isEmpty = !slot.record?.studentId;
          const query = searchQueries[slot.id] || '';
          const isSearchOpen = activeSearchSlotId === slot.id;
          const isMenuOpen = activeMenu === slot.id;
          const openMenuUp = index >= slots.length - 2;
          const filteredStudents = students.filter((option) => {
            const isAssigned = assignedStudentIds.has(option.id)
              || Boolean(option.accountId && assignedStudentIds.has(option.accountId))
              || Boolean(option.designerId && assignedStudentIds.has(option.designerId));
            if (isAssigned) return false;
            if (!query.trim()) return true;
            return option.name.toLowerCase().includes(query.trim().toLowerCase());
          });

          return (
            <RecordRow key={slot.id} $menuOpen={isMenuOpen}>
              <Cell $muted={isEmpty || isBlocked}>{slot.label}</Cell>
              <AttendanceCheckbox
                type="button"
                $checked={Boolean(slot.record?.isVisited)}
                disabled={isEmpty || isBlocked || isLoading}
                onClick={() => {
                  if (isEmpty || isBlocked || !slot.record?.studentId) return;
                  handleToggleAttendance(
                    slot.record.start || slot.id,
                    slot.record.studentId,
                    !slot.record.isVisited,
                  );
                }}
              >
                {slot.record?.isVisited && <CheckIcon />}
              </AttendanceCheckbox>
              {isBlocked ? (
                <Cell $muted>{t('lms.consultation.recordsModal.slotBlocked')}</Cell>
              ) : userName ? (
                <Cell>{userName}</Cell>
              ) : (
                <SearchWrap
                  ref={isSearchOpen ? searchWrapRef : undefined}
                >
                  <SearchBox>
                    <SearchIcon16 fill="#909090" />
                    <SearchInput
                      type="text"
                      value={query}
                      disabled={isLoading}
                      placeholder={t('lms.consultation.recordsModal.selectUser')}
                      onFocus={() => {
                        setActiveMenu(null);
                        setActiveSearchSlotId(slot.id);
                      }}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSearchQueries((prev) => ({ ...prev, [slot.id]: value }));
                        setActiveSearchSlotId(slot.id);
                      }}
                    />
                  </SearchBox>
                  {isSearchOpen && dropdownPosition && createPortal(
                    <Suggestions
                      ref={suggestionsRef}
                      $top={dropdownPosition.top}
                      $bottom={dropdownPosition.bottom}
                      $left={dropdownPosition.left}
                      $width={dropdownPosition.width}
                      $maxHeight={dropdownPosition.maxHeight}
                    >
                      {filteredStudents.length === 0 ? (
                        <SuggestionItem type="button" disabled>
                          {t('lms.consultation.recordsModal.noUsers')}
                        </SuggestionItem>
                      ) : (
                        filteredStudents.map((option) => (
                          <SuggestionItem
                            key={option.id}
                            type="button"
                            disabled={isLoading}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => handleScheduleStudent(
                              slot.id,
                              option.id,
                              ConsultationScheduleOperation.Add,
                              option,
                            )}
                          >
                            <SuggestionAvatar>
                              <Avatar avatar={option.avatar} fs={32} />
                            </SuggestionAvatar>
                            <SuggestionText>
                              <SuggestionName>{option.name}</SuggestionName>
                              {option.groupName ? (
                                <SuggestionGroup>{option.groupName}</SuggestionGroup>
                              ) : null}
                            </SuggestionText>
                          </SuggestionItem>
                        ))
                      )}
                    </Suggestions>,
                    document.body,
                  )}
                </SearchWrap>
              )}
              <Cell>{groupName}</Cell>
              {slot.record?.studentId && (
                <>
                  <MoreButton
                    type="button"
                    onClick={() => {
                      setActiveSearchSlotId(null);
                      setActiveMenu(isMenuOpen ? null : slot.id);
                    }}
                  >
                    <MoreIcon />
                  </MoreButton>
                  {isMenuOpen && (
                    <MenuButton
                      type="button"
                      $openUp={openMenuUp}
                      disabled={isLoading}
                      onClick={() => handleScheduleStudent(
                        slot.id,
                        isBlocked ? TIMESLOT_LOCKED : slot.record?.studentId,
                        ConsultationScheduleOperation.Remove,
                      )}
                    >
                      {isBlocked
                        ? t('lms.consultation.recordsModal.unblockRecord')
                        : t('lms.consultation.recordsModal.removeRecord')}
                    </MenuButton>
                  )}
                </>
              )}
              {!slot.record && (
                <>
                  <MoreButton
                    type="button"
                    onClick={() => {
                      setActiveSearchSlotId(null);
                      setActiveMenu(isMenuOpen ? null : slot.id);
                    }}
                  >
                    <MoreIcon />
                  </MoreButton>
                  {isMenuOpen && (
                    <MenuButton
                      type="button"
                      $openUp={openMenuUp}
                      disabled={isLoading}
                      onClick={() => handleScheduleStudent(
                        slot.id,
                        TIMESLOT_LOCKED,
                        ConsultationScheduleOperation.Add,
                      )}
                    >
                      {t('lms.consultation.recordsModal.blockRecord')}
                    </MenuButton>
                  )}
                </>
              )}
            </RecordRow>
          );
        })}
      </RecordsList>
    </ModalWrapper>
  );
};
import React, { FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { API, Designer, Endpoints, GroupAPI } from '@brg/api';
import { Avatar, useToasts } from '@brg/ui';
import MoreIcon from 'shared/icons/MoreIcon';
import SearchIcon16 from 'shared/icons/SearchIcon16';
import { ReactComponent as CheckIcon } from 'shared/assets/check.svg';
import { computeDesigner } from 'shared/helpers/computeData';
import { LayoutConst, useModal } from 'core';
import {
  ConsultationScheduleOperation,
  TIMESLOT_LOCKED,
  generateConsultationSlots,
  useConsultationScheduleStudent,
} from 'shared/hooks/api/LMS/useConsultationScheduleStudent';

interface ConsultationRecord {
  start: string;
  end?: string;
  studentId?: string;
  userName?: string;
  groupName?: string;
  isBlocked?: boolean;
  isVisited?: boolean;
}

interface ConsultationLesson {
  id?: string;
  title?: string | Array<{ lang: string; name: string }>;
  start?: string;
  end?: string;
  groupIds?: string[];
  studentsMaxLimit?: number | null;
  timeSlotPerRerson?: number | null;
  signUpSheetIds?: Record<string, string> | null;
  signUpSheetModels?: Record<string, Record<string, unknown>> | null;
  attendanceSheetIds?: Record<string, string> | null;
  attendanceSheetModels?: Record<string, Record<string, unknown>> | null;
  consultation?: {
    capacity?: number;
    timePerPerson?: number;
    records?: ConsultationRecord[];
    unavailableSlots?: string[];
  };
}

type StudentOption = {
  id: string;
  accountId?: string;
  designerId?: string;
  name: string;
  avatar?: string;
  groupName: string;
};

export interface ConsultationRecordsModalProps {
  lesson: ConsultationLesson;
  courseGroups?: Array<{ groupId: string; name?: Array<{ lang: string; name: string }> }>;
  teamId?: string;
  onSuccess?: () => void;
}

const ModalWrapper = styled.div`
  width: 704px;
  max-width: calc(100vw - 32px);
  padding: 24px 32px 32px;
  border-radius: 8px;
  background: #ffffff;

  @media (max-width: ${LayoutConst.screen.s}px) {
    max-width: calc(100vw - 16px);
    padding: 24px 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  margin: 0 0 8px;
  color: #151514;
  font-family: Inter;
  font-size: 34px;
  font-weight: 600;
  line-height: 41px;
  letter-spacing: -0.68px;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #151514;
  font-family: Inter;
  font-size: 17px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: -0.34px;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #909090;
  cursor: pointer;
  font-size: 36px;
  font-weight: 300;
  line-height: 24px;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

const RecordsList = styled.div`
  max-height: min(576px, calc(100vh - 190px));
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const RecordRow = styled.div<{ $menuOpen?: boolean }>`
  position: relative;
  z-index: ${({ $menuOpen }) => ($menuOpen ? 2 : 0)};
  display: grid;
  grid-template-columns: 100px 24px minmax(0, 1fr) 100px 40px;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  border-bottom: 1px solid #b6b6b5;
  padding: 4px 0;

  @media (max-width: ${LayoutConst.screen.s}px) {
    grid-template-columns: 84px 24px minmax(0, 1fr) 32px;

    & > :nth-child(4) {
      display: none;
    }
  }
`;

const Cell = styled.div<{ $muted?: boolean }>`
  overflow: hidden;
  color: ${({ $muted }) => ($muted ? '#b6b6b5' : '#151514')};
  font-family: Inter;
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.26px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SearchWrap = styled.div`
  position: relative;
  width: 100%;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 40px;
  padding: 0 16px;
  border-radius: 3px;
  background: #f2f2f2;
  color: #909090;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: #151514;
  font-family: Inter;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: -0.26px;

  &::placeholder {
    color: #909090;
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const Suggestions = styled.div<{
  $top?: number;
  $bottom?: number;
  $left: number;
  $width: number;
  $maxHeight: number;
}>`
  position: fixed;
  z-index: 200001;
  top: ${({ $top }) => ($top == null ? 'auto' : `${$top}px`)};
  bottom: ${({ $bottom }) => ($bottom == null ? 'auto' : `${$bottom}px`)};
  left: ${({ $left }) => `${$left}px`};
  width: ${({ $width }) => `${$width}px`};
  max-height: ${({ $maxHeight }) => `${$maxHeight}px`};
  overflow-y: auto;
  border-radius: 3px;
  background: #ffffff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  scrollbar-width: thin;
  scrollbar-color: #b6b6b5 #e7e7e7;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #e7e7e7;
  }

  &::-webkit-scrollbar-thumb {
    background: #b6b6b5;
    border-radius: 2px;
  }
`;

const SuggestionItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: 0;
  background: transparent;
  color: #151514;
  font-family: Inter;
  font-size: 13px;
  line-height: 16px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f2f2f2;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const SuggestionAvatar = styled.div`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
`;

const SuggestionText = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
`;

const SuggestionName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SuggestionGroup = styled.span`
  overflow: hidden;
  color: #909090;
  font-size: 11px;
  line-height: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AttendanceCheckbox = styled.button<{ $checked?: boolean }>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid ${({ $checked }) => ($checked ? '#909090' : '#b6b6b5')};
  border-radius: 3px;
  background: #fefefe;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

const MoreButton = styled.button`
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #909090;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  .moreIcon {
    width: 24px;
    height: 24px;
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border: 0;
    box-shadow: none;
  }
`;

const MenuButton = styled.button<{ $openUp?: boolean }>`
  position: absolute;
  z-index: 1;
  right: 0;
  ${({ $openUp }) => ($openUp
    ? 'bottom: calc(100% - 1px); top: auto;'
    : 'top: calc(100% - 1px); bottom: auto;')}
  width: 240px;
  padding: 12px 16px;
  border: 0;
  background: #f2f2f2;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.12);
  color: #70706f;
  font-family: Inter;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;

  &:focus,
  &:focus-visible {
    outline: none;
    border: 0;
    box-shadow: none;
  }
`;

const getTitle = (lesson: ConsultationLesson, language: string) => {
  if (typeof lesson.title === 'string') return lesson.title;

  return lesson.title?.find((item) => item.lang === language.toLowerCase())?.name
    || lesson.title?.[0]?.name
    || '';
};

const getStudentId = (student: Record<string, unknown>) =>
  String(student.accountId || student.designerId || student.id || '');

const getStudentName = (student: Record<string, unknown>, language: string) => {
  const localized = API.toLang(student, language) as Designer;
  const computed = computeDesigner(localized, { showFullName: true });
  const computedName = [computed.surName, computed.name, computed.middleName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  if (computedName) return computedName;

  if (typeof student.fullName === 'string' && student.fullName.trim()) return student.fullName.trim();
  if (typeof student.name === 'string' && student.name.trim()) return student.name.trim();
  if (Array.isArray(student.name)) {
    return String((student.name[0] as { name?: string } | undefined)?.name || '').trim();
  }

  return [student.lastName, student.firstName, student.middleName]
    .filter((value): value is string => typeof value === 'string' && Boolean(value.trim()))
    .join(' ');
};

const mapStudentOption = (student: Designer<'Global'> | Designer<'Local'>, groupName: string, language: string): StudentOption | null => {
  const localized = API.toLang(student, language) as Designer;
  const raw = localized as unknown as Record<string, unknown>;
  const computed = computeDesigner(localized, { showFullName: true });
  const id = getStudentId(raw);
  const name = getStudentName(raw, language);

  if (!id || !name) return null;

  return {
    id,
    accountId: raw.accountId ? String(raw.accountId) : undefined,
    designerId: raw.designerId ? String(raw.designerId) : undefined,
    name,
    avatar: computed.avatar,
    groupName,
  };
};

const isRecordVisited = (lesson: ConsultationLesson, timeSlot: string) => Boolean(
  lesson.attendanceSheetIds?.[timeSlot] || lesson.attendanceSheetModels?.[timeSlot],
);

const findStudentOption = (students: StudentOption[], studentId?: string) => {
  if (!studentId) return null;
  return students.find((item) => (
    item.id === studentId
    || item.accountId === studentId
    || item.designerId === studentId
  )) || null;
};

const buildRecordsFromLesson = (lesson: ConsultationLesson, language: string): ConsultationRecord[] => {
  const models = Object.entries(lesson.signUpSheetModels || {}).map(([timeSlot, student]) => ({
    start: timeSlot,
    studentId: getStudentId(student) || undefined,
    userName: getStudentName(student, language) || undefined,
    isVisited: isRecordVisited(lesson, timeSlot),
  }));
  const modelSlotTimes = new Set(
    models
      .map((record) => new Date(record.start).getTime())
      .filter((value) => !Number.isNaN(value)),
  );
  const ids = Object.entries(lesson.signUpSheetIds || {})
    .map(([timeSlot, studentId]) => ({
      start: timeSlot,
      studentId,
      isVisited: isRecordVisited(lesson, timeSlot),
    }))
    .filter((record) => {
      const time = new Date(record.start).getTime();
      return !Number.isNaN(time) && !modelSlotTimes.has(time);
    });

  return [...models, ...ids];
};

export const ConsultationRecordsModal: FC<ConsultationRecordsModalProps> = ({
  lesson,
  courseGroups = [],
  teamId,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation();
  const modal = useModal();
  const toasts = useToasts();
  const { scheduleStudent, isLoading } = useConsultationScheduleStudent();
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [activeSearchSlotId, setActiveSearchSlotId] = useState<string | null>(null);
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const [students, setStudents] = useState<StudentOption[]>([]);
  const [localRecords, setLocalRecords] = useState<ConsultationRecord[]>(() => (
    buildRecordsFromLesson(lesson, i18n.language)
  ));
  const [dropdownPosition, setDropdownPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const searchWrapRef = useRef<HTMLDivElement | null>(null);
  const suggestionsRef = useRef<HTMLDivElement | null>(null);
  const recordsListRef = useRef<HTMLDivElement | null>(null);
  const start = useMemo(() => (lesson.start ? new Date(lesson.start) : null), [lesson.start]);
  const duration = lesson.consultation?.timePerPerson || lesson.timeSlotPerRerson || 15;
  const maxSlots = lesson.consultation?.capacity ?? lesson.studentsMaxLimit ?? null;
  const specificGroupIds = useMemo(
    () => (lesson.groupIds || []).filter(Boolean),
    [lesson.groupIds],
  );

  const updateDropdownPosition = () => {
    const anchor = searchWrapRef.current;
    if (!anchor) {
      setDropdownPosition(null);
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const gap = 4;
    const preferredHeight = 220;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < preferredHeight && spaceAbove > spaceBelow;
    const maxHeight = Math.max(80, Math.min(preferredHeight, openUp ? spaceAbove : spaceBelow));

    if (openUp) {
      setDropdownPosition({
        bottom: window.innerHeight - rect.top + gap,
        left: rect.left,
        width: rect.width,
        maxHeight,
      });
      return;
    }

    setDropdownPosition({
      top: rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      maxHeight,
    });
  };

  useEffect(() => {
    setLocalRecords(buildRecordsFromLesson(lesson, i18n.language));
  }, [i18n.language, lesson.signUpSheetIds, lesson.signUpSheetModels, lesson.attendanceSheetIds, lesson.attendanceSheetModels]);

  useEffect(() => {
    let isCancelled = false;

    const loadStudents = async () => {
      try {
        if (specificGroupIds.length > 0) {
          const results = await Promise.all(specificGroupIds.map(async (groupId) => {
            const response = await GroupAPI.GetMembersPaging({ groupId, limit: 0, page: 0 });
            const data = await response.json();
            const group = courseGroups.find((item) => item.groupId === groupId);
            const groupName = group?.name?.find((item) => item.lang === i18n.language)?.name
              || group?.name?.[0]?.name
              || '';

            return (data?.data || [])
              .map((student: Designer<'Global'> | Designer<'Local'>) => mapStudentOption(student, groupName, i18n.language))
              .filter((student: StudentOption | null): student is StudentOption => Boolean(student));
          }));

          if (isCancelled) return;
          const uniqueStudents = new Map<string, StudentOption>();
          results.flat().forEach((student) => uniqueStudents.set(student.id, student));
          setStudents(Array.from(uniqueStudents.values()));
          return;
        }

        if (!teamId) {
          if (!isCancelled) setStudents([]);
          return;
        }

        // One request: course groups + members, so we can show group name for each student.
        const response = await API.request()
          .endpoint(Endpoints.geograffee)
          .url('/api/LMS/GetGroupsInfoByCourseId')
          .addQuery('teamId', teamId)
          .get()
          .send();
        const data = await response.json();

        if (isCancelled) return;

        const uniqueStudents = new Map<string, StudentOption>();
        const resolveGroupName = (group: Record<string, unknown>) => {
          const fromCourse = courseGroups.find((item) => item.groupId === group.id || item.groupId === group.groupId);
          if (fromCourse) {
            return fromCourse.name?.find((item) => item.lang === i18n.language)?.name
              || fromCourse.name?.[0]?.name
              || '';
          }

          if (typeof group.title === 'string') return group.title;
          if (Array.isArray(group.name)) {
            const names = group.name as Array<{ lang?: string; name?: string }>;
            return names.find((item) => item.lang === i18n.language)?.name
              || names[0]?.name
              || '';
          }
          if (typeof group.name === 'string') return group.name;
          return '';
        };

        (data?.groups || []).forEach((group: Record<string, unknown>) => {
          const groupName = resolveGroupName(group);
          const members = (group.members || []) as Array<Designer<'Global'> | Designer<'Local'>>;
          members.forEach((student) => {
            const option = mapStudentOption(student, groupName, i18n.language);
            if (option) uniqueStudents.set(option.id, option);
          });
        });

        ((data?.noneGroupMembers || []) as Array<Designer<'Global'> | Designer<'Local'>>).forEach((student) => {
          const option = mapStudentOption(student, '', i18n.language);
          if (option && !uniqueStudents.has(option.id)) {
            uniqueStudents.set(option.id, option);
          }
        });

        setStudents(Array.from(uniqueStudents.values()));
      } catch {
        if (!isCancelled) setStudents([]);
      }
    };

    loadStudents();

    return () => {
      isCancelled = true;
    };
  }, [courseGroups, i18n.language, specificGroupIds, teamId]);

  useEffect(() => {
    if (!students.length) return;

    setLocalRecords((prev) => prev.map((record) => {
      if (!record.studentId || record.studentId === TIMESLOT_LOCKED) return record;
      if (record.userName && record.groupName) return record;

      const student = findStudentOption(students, record.studentId);
      if (!student) return record;

      return {
        ...record,
        userName: record.userName || student.name,
        groupName: record.groupName || student.groupName,
      };
    }));
  }, [students]);

  useLayoutEffect(() => {
    if (!activeSearchSlotId) {
      setDropdownPosition(null);
      return undefined;
    }

    updateDropdownPosition();

    const handleReposition = () => updateDropdownPosition();
    const listNode = recordsListRef.current;

    window.addEventListener('resize', handleReposition);
    listNode?.addEventListener('scroll', handleReposition, true);
    document.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      listNode?.removeEventListener('scroll', handleReposition, true);
      document.removeEventListener('scroll', handleReposition, true);
    };
  }, [activeSearchSlotId, searchQueries, students]);

  useEffect(() => {
    if (!activeSearchSlotId) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (searchWrapRef.current?.contains(target) || suggestionsRef.current?.contains(target)) {
        return;
      }
      setActiveSearchSlotId(null);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [activeSearchSlotId]);

  const slots = useMemo(() => {
    return generateConsultationSlots({
      start: lesson.start,
      end: lesson.end,
      durationMinutes: duration,
      maxSlots,
      referenceStart: lesson.start,
    }).map((slot) => ({
      ...slot,
      record: localRecords.find((item) => {
        const recordStart = new Date(item.start);
        return !Number.isNaN(recordStart.getTime()) && recordStart.getTime() === slot.time;
      }),
    }));
  }, [duration, lesson.end, lesson.start, localRecords, maxSlots]);

  const assignedStudentIds = useMemo(
    () => new Set(
      localRecords
        .map((record) => record.studentId)
        .filter((studentId): studentId is string => Boolean(studentId) && studentId !== TIMESLOT_LOCKED),
    ),
    [localRecords],
  );

  const date = start?.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }) || '';

  const handleScheduleStudent = async (
    timeSlot: string,
    studentId: string | undefined,
    operation: ConsultationScheduleOperation,
    studentMeta?: StudentOption,
  ) => {
    if (!lesson.id || (operation === ConsultationScheduleOperation.Add && !studentId)) return;

    const result = await scheduleStudent({
      lessonId: lesson.id,
      timeSlot,
      studentId: studentId ?? '',
      operation,
    });

    if (!result.success) {
      toasts.error(t('lms.consultation.recordsModal.error'));
      return;
    }

    if (operation === ConsultationScheduleOperation.Remove) {
      setLocalRecords((prev) => prev.filter((record) => record.start !== timeSlot));
    } else if (studentId === TIMESLOT_LOCKED) {
      setLocalRecords((prev) => [
        ...prev.filter((record) => record.start !== timeSlot),
        { start: timeSlot, studentId: TIMESLOT_LOCKED },
      ]);
    } else if (studentId) {
      setLocalRecords((prev) => [
        ...prev.filter((record) => record.start !== timeSlot),
        {
          start: timeSlot,
          studentId,
          userName: studentMeta?.name,
          groupName: studentMeta?.groupName,
        },
      ]);
    }

    setActiveMenu(null);
    setActiveSearchSlotId(null);
    setSearchQueries((prev) => {
      if (!(timeSlot in prev)) return prev;
      const next = { ...prev };
      delete next[timeSlot];
      return next;
    });
    toasts.info(t('lms.consultation.recordsModal.success'));
    onSuccess?.();
  };

  const handleToggleAttendance = async (timeSlot: string, studentId: string, nextVisited: boolean) => {
    if (!lesson.id || !studentId || studentId === TIMESLOT_LOCKED) return;

    setLocalRecords((prev) => prev.map((record) => (
      record.start === timeSlot ? { ...record, isVisited: nextVisited } : record
    )));

    const result = await scheduleStudent({
      lessonId: lesson.id,
      timeSlot,
      studentId,
      operation: nextVisited
        ? ConsultationScheduleOperation.Add
        : ConsultationScheduleOperation.Remove,
    }, '/api/LMS/ConsultationAttendanceStudent');

    if (!result.success) {
      setLocalRecords((prev) => prev.map((record) => (
        record.start === timeSlot ? { ...record, isVisited: !nextVisited } : record
      )));
      toasts.error(t('lms.consultation.recordsModal.error'));
    }
  };

  return (
    <ModalWrapper>
      <Header>
        <div>
          <Title>{t('lms.consultation.recordsModal.title')}</Title>
          <Subtitle>
            {[getTitle(lesson, i18n.language), date]
              .map((part) => part?.trim())
              .filter(Boolean)
              .join('. ')}
          </Subtitle>
        </div>
        <CloseButton type="button" onClick={() => modal.hide()} aria-label="close">×</CloseButton>
      </Header>

      <RecordsList ref={recordsListRef}>
        {slots.map((slot, index) => {
          const isBlocked = slot.record?.studentId === TIMESLOT_LOCKED;
          const student = findStudentOption(students, slot.record?.studentId);
          const userName = slot.record?.userName || student?.name;
          const groupName = slot.record?.groupName || student?.groupName || '';
          const isEmpty = !slot.record?.studentId;
          const query = searchQueries[slot.id] || '';
          const isSearchOpen = activeSearchSlotId === slot.id;
          const isMenuOpen = activeMenu === slot.id;
          const openMenuUp = index >= slots.length - 2;
          const filteredStudents = students.filter((option) => {
            const isAssigned = assignedStudentIds.has(option.id)
              || Boolean(option.accountId && assignedStudentIds.has(option.accountId))
              || Boolean(option.designerId && assignedStudentIds.has(option.designerId));
            if (isAssigned) return false;
            if (!query.trim()) return true;
            return option.name.toLowerCase().includes(query.trim().toLowerCase());
          });

          return (
            <RecordRow key={slot.id} $menuOpen={isMenuOpen}>
              <Cell $muted={isEmpty || isBlocked}>{slot.label}</Cell>
              <AttendanceCheckbox
                type="button"
                $checked={Boolean(slot.record?.isVisited)}
                disabled={isEmpty || isBlocked || isLoading}
                onClick={() => {
                  if (isEmpty || isBlocked || !slot.record?.studentId) return;
                  handleToggleAttendance(
                    slot.record.start || slot.id,
                    slot.record.studentId,
                    !slot.record.isVisited,
                  );
                }}
              >
                {slot.record?.isVisited && <CheckIcon />}
              </AttendanceCheckbox>
              {isBlocked ? (
                <Cell $muted>{t('lms.consultation.recordsModal.slotBlocked')}</Cell>
              ) : userName ? (
                <Cell>{userName}</Cell>
              ) : (
                <SearchWrap
                  ref={isSearchOpen ? searchWrapRef : undefined}
                >
                  <SearchBox>
                    <SearchIcon16 fill="#909090" />
                    <SearchInput
                      type="text"
                      value={query}
                      disabled={isLoading}
                      placeholder={t('lms.consultation.recordsModal.selectUser')}
                      onFocus={() => {
                        setActiveMenu(null);
                        setActiveSearchSlotId(slot.id);
                      }}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSearchQueries((prev) => ({ ...prev, [slot.id]: value }));
                        setActiveSearchSlotId(slot.id);
                      }}
                    />
                  </SearchBox>
                  {isSearchOpen && dropdownPosition && createPortal(
                    <Suggestions
                      ref={suggestionsRef}
                      $top={dropdownPosition.top}
                      $bottom={dropdownPosition.bottom}
                      $left={dropdownPosition.left}
                      $width={dropdownPosition.width}
                      $maxHeight={dropdownPosition.maxHeight}
                    >
                      {filteredStudents.length === 0 ? (
                        <SuggestionItem type="button" disabled>
                          {t('lms.consultation.recordsModal.noUsers')}
                        </SuggestionItem>
                      ) : (
                        filteredStudents.map((option) => (
                          <SuggestionItem
                            key={option.id}
                            type="button"
                            disabled={isLoading}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => handleScheduleStudent(
                              slot.id,
                              option.id,
                              ConsultationScheduleOperation.Add,
                              option,
                            )}
                          >
                            <SuggestionAvatar>
                              <Avatar avatar={option.avatar} fs={32} />
                            </SuggestionAvatar>
                            <SuggestionText>
                              <SuggestionName>{option.name}</SuggestionName>
                              {option.groupName ? (
                                <SuggestionGroup>{option.groupName}</SuggestionGroup>
                              ) : null}
                            </SuggestionText>
                          </SuggestionItem>
                        ))
                      )}
                    </Suggestions>,
                    document.body,
                  )}
                </SearchWrap>
              )}
              <Cell>{groupName}</Cell>
              {slot.record?.studentId && (
                <>
                  <MoreButton
                    type="button"
                    onClick={() => {
                      setActiveSearchSlotId(null);
                      setActiveMenu(isMenuOpen ? null : slot.id);
                    }}
                  >
                    <MoreIcon />
                  </MoreButton>
                  {isMenuOpen && (
                    <MenuButton
                      type="button"
                      $openUp={openMenuUp}
                      disabled={isLoading}
                      onClick={() => handleScheduleStudent(
                        slot.id,
                        isBlocked ? TIMESLOT_LOCKED : slot.record?.studentId,
                        ConsultationScheduleOperation.Remove,
                      )}
                    >
                      {isBlocked
                        ? t('lms.consultation.recordsModal.unblockRecord')
                        : t('lms.consultation.recordsModal.removeRecord')}
                    </MenuButton>
                  )}
                </>
              )}
              {!slot.record && (
                <>
                  <MoreButton
                    type="button"
                    onClick={() => {
                      setActiveSearchSlotId(null);
                      setActiveMenu(isMenuOpen ? null : slot.id);
                    }}
                  >
                    <MoreIcon />
                  </MoreButton>
                  {isMenuOpen && (
                    <MenuButton
                      type="button"
                      $openUp={openMenuUp}
                      disabled={isLoading}
                      onClick={() => handleScheduleStudent(
                        slot.id,
                        TIMESLOT_LOCKED,
                        ConsultationScheduleOperation.Add,
                      )}
                    >
                      {t('lms.consultation.recordsModal.blockRecord')}
                    </MenuButton>
                  )}
                </>
              )}
            </RecordRow>
          );
        })}
      </RecordsList>
    </ModalWrapper>
  );
};
